import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiStar, FiChevronLeft, FiChevronRight, FiUsers } from 'react-icons/fi';

const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6496FF;
  margin-bottom: 1.5rem;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #E8EAF0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TestimonialContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
`;

const TestimonialCard = styled(motion.div)`
  background: rgba(25, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 3rem;
  backdrop-filter: blur(20px);
  text-align: center;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const QuoteIcon = styled.div`
  font-size: 3rem;
  color: rgba(100, 150, 255, 0.3);
  margin-bottom: 1.5rem;
`;

const TestimonialText = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #FFFFFF;
  margin-bottom: 2rem;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const UserAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6496FF 0%, #4678FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
`;

const UserDetails = styled.div`
  text-align: left;
`;

const UserName = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #FFFFFF;
  margin-bottom: 0.25rem;
`;

const UserRole = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
`;

const Rating = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const Star = styled(FiStar)`
  color: #FFD700;
  fill: #FFD700;
  font-size: 1.25rem;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const NavButton = styled(motion.button)`
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  color: #6496FF;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(100, 150, 255, 0.2);
    border-color: rgba(100, 150, 255, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Indicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#6496FF' : 'rgba(100, 150, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #6496FF;
  }
`;

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const testimonials = [
    {
      text: "As a consultant, I'm constantly in client meetings where I need to respond confidently. Meeting Copilot is like having a brilliant teammate who never misses a detail.",
      name: "Sarah Chen",
      role: "Senior Consultant at McKinsey & Company",
      avatar: "SC",
      rating: 5
    },
    {
      text: "During technical sales calls, customers ask tough questions. Now I can focus on building relationships while my AI copilot handles the complex details instantly.",
      name: "Marcus Rodriguez",
      role: "Sales Engineer at Salesforce",
      avatar: "MR",
      rating: 5
    },
    {
      text: "Customer success calls can get intense. Having an AI that understands the context and helps me respond professionally has been a game-changer for my confidence.",
      name: "Emily Johnson",
      role: "VP Customer Success at HubSpot",
      avatar: "EJ",
      rating: 5
    },
    {
      text: "As a founder, I jump between investor meetings, customer calls, and team syncs. My AI copilot helps me stay sharp and never lose the thread.",
      name: "David Kim",
      role: "CEO & Founder at TechStart",
      avatar: "DK",
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  return (
    <TestimonialsSection id="testimonials" ref={ref}>
      <Container>
        <SectionHeader>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <FiUsers />
            Testimonials
          </Badge>
          
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Trusted by High-Stakes Professionals
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            See how Meeting Copilot helps professionals in critical roles respond with confidence
          </Subtitle>
        </SectionHeader>

        <TestimonialContainer>
          <AnimatePresence mode="wait">
            <TestimonialCard
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <QuoteIcon>"</QuoteIcon>
              
              <TestimonialText>
                {testimonials[currentIndex].text}
              </TestimonialText>
              
              <UserInfo>
                <UserAvatar>
                  {testimonials[currentIndex].avatar}
                </UserAvatar>
                <UserDetails>
                  <UserName>{testimonials[currentIndex].name}</UserName>
                  <UserRole>{testimonials[currentIndex].role}</UserRole>
                </UserDetails>
              </UserInfo>
              
              <Rating>
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star key={i} />
                ))}
              </Rating>
            </TestimonialCard>
          </AnimatePresence>

          <Navigation>
            <NavButton
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevTestimonial}
            >
              <FiChevronLeft />
            </NavButton>
            
            <NavButton
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextTestimonial}
            >
              <FiChevronRight />
            </NavButton>
          </Navigation>

          <Indicators>
            {testimonials.map((_, index) => (
              <Indicator
                key={index}
                active={index === currentIndex}
                onClick={() => goToTestimonial(index)}
              />
            ))}
          </Indicators>
        </TestimonialContainer>
      </Container>
    </TestimonialsSection>
  );
};

export default Testimonials; 